'use client';

import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

const MAP_EMBED_SRC =
  'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d6020.275570061716!2d28.914183999999988!3d41.02224139999999!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0xe4b7eafb8a3fd6d6!2swww.filizkozmetik.com!5e0!3m2!1str!2str!4v1584350237487!5m2!1str!2str';

type LazyMapEmbedProps = {
  title: string;
  className?: string;
  /** Sabit yükseklik (px), ana sayfa iletişim kartı */
  height?: number;
  /** Üst konteyner yüksekliğini doldur (ör. iletişim sayfası) */
  fill?: boolean;
};

/**
 * Harita iframe'ini yalnızca görünür alana yaklaşınca yükler; ilk yüklemede Google Maps JS indirilmez.
 */
export function LazyMapEmbed({ title, className, height = 300, fill = false }: LazyMapEmbedProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [load, setLoad] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setLoad(true);
          obs.disconnect();
        }
      },
      { rootMargin: '240px', threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  if (fill) {
    return (
      <div ref={containerRef} className={cn('relative h-full min-h-[200px] w-full', className)}>
        {load ? (
          <iframe
            title={title}
            src={MAP_EMBED_SRC}
            width="100%"
            height="100%"
            className="absolute inset-0 h-full w-full rounded-lg"
            style={{ border: 0 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center rounded-lg bg-gray-100 text-sm text-gray-500"
            aria-hidden
          >
            Harita yükleniyor…
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn('w-full overflow-hidden', className)}
      style={{ height }}
    >
      {load ? (
        <iframe
          title={title}
          src={MAP_EMBED_SRC}
          width="100%"
          height={height}
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      ) : (
        <div
          className="flex h-full w-full items-center justify-center bg-gray-100 text-sm text-gray-500"
          style={{ height }}
          aria-hidden
        >
          Harita yükleniyor…
        </div>
      )}
    </div>
  );
}
